/**
 * Map TradeStation / Connect Trade tickers onto Yahoo Finance chart symbols.
 *
 * Yahoo's v8 chart API is unofficial. Equities usually match 1:1. Indexes,
 * crypto, forex, and futures use Yahoo-specific suffixes (`^GSPC`, `BTC-USD`,
 * `EURUSD=X`, `ES=F`, `ESH26.CME`).
 */

const INDEX_SYMBOLS = {
  SPX: '^GSPC',
  SPX_X: '^GSPC',
  COMP: '^IXIC',
  COMPX: '^IXIC',
  COMP_X: '^IXIC',
  NDX: '^NDX',
  NDX_X: '^NDX',
  DJI: '^DJI',
  DJX: '^DJI',
  DJI_X: '^DJI',
  RUT: '^RUT',
  RUT_X: '^RUT',
  VIX: '^VIX',
  VIX_X: '^VIX',
};

const CRYPTO_USD = new Set(['BTC', 'ETH', 'SOL', 'DOGE', 'ADA', 'XRP', 'LTC', 'AVAX', 'LINK', 'DOT']);

const FUTURES_EXCHANGE = {
  ES: 'CME',
  NQ: 'CME',
  YM: 'CBT',
  RTY: 'CME',
  MES: 'CME',
  MNQ: 'CME',
  MYM: 'CBT',
  M2K: 'CME',
  CL: 'NYM',
  MCL: 'NYM',
  NG: 'NYM',
  GC: 'CMX',
  SI: 'CMX',
  HG: 'CMX',
  MGC: 'CMX',
  ZB: 'CBT',
  ZN: 'CBT',
  ZF: 'CBT',
  ZT: 'CBT',
  ZC: 'CBT',
  ZS: 'CBT',
  ZW: 'CBT',
};

const CONTINUOUS_FUTURES = new Set(Object.keys(FUTURES_EXCHANGE));

const FUTURES_CONTRACT_RE = /^([A-Z0-9]{1,4})([FGHJKMNQUVXZ])(\d{2})$/;

function stripTsDecorators(raw) {
  let symbol = String(raw || '').trim().toUpperCase();
  if (!symbol) return '';
  if (symbol.startsWith('@')) symbol = symbol.slice(1);
  // TradeStation cash indexes: $SPX.X / $SPX
  if (symbol.startsWith('$')) symbol = symbol.slice(1);
  if (symbol.endsWith('.X')) symbol = symbol.slice(0, -2);
  return symbol;
}

function mapIndex(symbol) {
  if (INDEX_SYMBOLS[symbol]) return INDEX_SYMBOLS[symbol];
  const underscored = symbol.replace('.', '_');
  return INDEX_SYMBOLS[underscored] || null;
}

function cryptoBase(symbol) {
  if (!symbol) return '';
  if (symbol.endsWith('-USD')) return symbol.slice(0, -4);
  if (symbol.endsWith('USD') && symbol.length > 3) return symbol.slice(0, -3);
  return symbol;
}

/**
 * True for 24/7 crypto pairs (BTCUSD, ETH-USD, …). Intraday overlays must fetch
 * extra history so weekend/overnight bars still cover the equity session window.
 */
export function isYahooCryptoTicker(ticker) {
  const symbol = stripTsDecorators(ticker);
  if (!symbol) return false;
  return CRYPTO_USD.has(cryptoBase(symbol));
}

/**
 * @param {string} ticker
 * @returns {string} Yahoo Finance symbol
 */
export function toYahooSymbol(ticker) {
  const symbol = stripTsDecorators(ticker);
  if (!symbol) return '';

  const index = mapIndex(symbol);
  if (index) return index;

  // Class shares: BRK.B / BRK/B → BRK-B (Yahoo).
  const classShare = symbol.match(/^([A-Z]{1,5})[./]([A-Z])$/);
  if (classShare) return `${classShare[1]}-${classShare[2]}`;

  if (CRYPTO_USD.has(symbol.replace(/USD$/, '')) && symbol.endsWith('USD') && symbol.length > 3) {
    return `${symbol.slice(0, -3)}-USD`;
  }

  // Major FX: EURUSD → EURUSD=X
  if (/^[A-Z]{6}$/.test(symbol) && /USD|EUR|GBP|JPY|AUD|CAD|CHF|NZD/.test(symbol.slice(0, 3))) {
    return `${symbol}=X`;
  }

  const contract = symbol.match(FUTURES_CONTRACT_RE);
  if (contract && FUTURES_EXCHANGE[contract[1]]) {
    return `${symbol}.${FUTURES_EXCHANGE[contract[1]]}`;
  }

  if (CONTINUOUS_FUTURES.has(symbol)) {
    return `${symbol}=F`;
  }

  return symbol;
}
